import { autoinject, bindable, observable, computedFrom, containerless } from 'aurelia-framework';
import { isEmpty, keys } from 'lodash';
import {
	IImportAM,
	IImportItemAM,
	IImportMappingErrorAM,
	ImportItemStatus,
	ImportMappingErrorSeverity,
	ImportService
} from './import-api';
import { IImportItemList, ImportItem } from './import-item';
import { ITargetToServiceAM, LinkedMappingsService } from '../services/service-targetings/linked-mappings-service';
import { NotificationType, ResourcedNotificationService } from '../../components/notifications/notification-service';

@autoinject
export class ImportItemList implements IImportItemList {
	@bindable importEntry: IImportAM;
	@bindable fiscalYearId: string;
	@bindable serviceStructureId: string;
	@bindable getImport: () => void;

	@observable filteredItems: ImportItem[];
	filter: ImportItemStatus = ImportItemStatus.Error;
	@observable dimensions: string[];

	itemsToTarget: ITargetToServiceAM[] = [];

	maxItemsToShow: number = 1000;

	@computedFrom('importEntry')
	get importTypeName(): string {

		return this.importEntry.type == 'FinancialEntries' ? 'FinancialEntry' : 'MeasureEntry';
	}

	@computedFrom('importEntry.statusCounts')
	get isRemapEnabled(): boolean {
		return this.importEntry.statusCounts[ImportItemStatus.Error] +
			this.importEntry.statusCounts[ImportItemStatus.Rejected] +
			this.importEntry.statusCounts[ImportItemStatus.Staged] +
			this.importEntry.statusCounts[ImportItemStatus.Warning]
			> 0;
	}

	@computedFrom('importEntry')
	get isAutoTargetEnabled(): boolean {
		return this.importEntry.type === 'MeasureEntries';
	}

	@computedFrom('filteredItems')
	get showServiceColumn(): boolean {
		return this.filteredItems && !!this.filteredItems.find(i => !!i.model.serviceName);
	}

	@computedFrom('filteredItems')
	get showMunicipalityColumn(): boolean {
		return this.filteredItems && !!this.filteredItems.find(i => !!i.model.municipalityName);
	}

	constructor(public importService: ImportService, public linkedMappingService: LinkedMappingsService, public notificationService: ResourcedNotificationService) {
	}

	async loadView() {
		await this.getItemsByStatus(this.filter);
		this.updateDimensions();
	}

	updateDimensions() {

		let result: string[] = [];
		this.filteredItems.forEach(i => {
			i.model.dimensionValues.forEach(v => {
				if (!result.find(s => s === v.dimensionName)) {
					result.push(v.dimensionName);
				}
			})
		})

		this.dimensions = result;
	}

	getDimensionValue(item: ImportItem, dimensionName: string) {
		return item.model.dimensionValues.find(v => v.dimensionName === dimensionName);
	}

	initializeFilter() {
		switch (this.importEntry.status) {
			case 'Staged':
				this.filter = ImportItemStatus.Error;
				break;
			case 'Archived':
				this.filter = ImportItemStatus.Imported;
				break;
			default:
				throw new Error(`Import status ${this.importEntry.status} is not supported.`);
		}
	}

	onReject() { }

	onUndo() { }

	async changeFilter(filter: ImportItemStatus) {
		this.filter = filter;
		await this.getItemsByStatus(this.filter);
		this.updateDimensions();
	}

	async cancelImport() {;
		const response = await this.importService.cancelImport(this.importEntry.id);
		if (response.ok) {
			location.href = '/imports';
		}
	}

	async cancelImportKeepAutomationResults() {
		const response = await this.importService.cancelImportKeepAutomationResults(this.importEntry.id);
		if (response.ok) {
			location.href = '/imports';
		}
	}

	async remapImport() {
		const response = await this.importService.remapImport(this.importEntry.id);
		if (response.ok) {
			this.getImport();
		}
	}

	async executeImport(): Promise<boolean> {
		const response = await this.importService.executeImport(this.importEntry.id);
		if (response.ok) {
			location.href = '/imports';
			return true;
		}

		// A held or already processed import: the server's message is localized
		const body = await response.json().catch(() => null);
		this.notificationService.pushWithLiteralContent("ExecuteImport_ErrorTitle", NotificationType.Error, body?.message ?? '');
		return false;
	}

	async executeAutoTarget() {
		const response = await this.linkedMappingService.updateMappings(this.fiscalYearId, this.serviceStructureId, this.itemsToTarget);
		if (!response.ok) {
			const body = await response.json();
			// API returns a bad request with the message below when the service is not found in the service structure
			const content = body.message == "Some of the given services are not found in the service structure!" ? "ExecuteAutoTarget_ErrorContent" : null;
			this.notificationService.push("ExecuteAutoTarget_ErrorTitle", NotificationType.Error, content);
			return;
		}

		await this.remapImport();
		this.notificationService.push("ExecuteAutoTarget_SuccessTitle", NotificationType.Success, null);
	}

	async getItemsByStatus(status: ImportItemStatus) {

		this.filteredItems = null;

		const ams = await this.importService.getImportItemsByStatus(this.importEntry.id, status, this.maxItemsToShow);
		this.filteredItems = ams.map(am => new ImportItem({
			model: am,
			viewModel: this
		}));

		// List of items with a service mapping error that can be auto-targeted
		this.itemsToTarget = this.filteredItems
			.filter(i => i.model.mappingErrors.Service && i.model.mappingErrors.Service.length > 0)
			.map(i => ({ serviceId: i.model.serviceId, costCenterId: i.model.costCenterId, serviceTargetingId: null }));
	}

	private importEntryChanged(newValue: IImportAM) {
		this.initializeFilter();
		this.loadView();
	}
}

